import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useTheme,
  Alert,
  Button,
  Stack,
  TablePagination,
  LinearProgress,
  Tooltip,
  alpha,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isBefore, startOfDay } from 'date-fns';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Inbox as InboxIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarMonthOutlined as CalendarIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import { useSelfSetupTour } from '../../hooks/useSelfSetupTour';
import { elevatedPanelSx } from '../../constants/shape';
import StatusChip from '../../components/StatusChip';
import { getOperationalStatusColor } from '../../constants/status-colors';
import { usePickupOrderStore } from '../../stores/pickup_order.store';
import { useOrganizationStore } from '../../stores/organization.store';
import { useCollectionSiteStore } from '../../stores/collection-site.store';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useConfirm } from '../../hooks/useConfirm';
import {
  PERM_PICKUP_CREATE,
  PERM_PICKUP_WRITE,
  PICKUP_ORDER_WRITE_ROLES,
} from '../../auth';
import { OrderSchema, PickupOrderStatus } from '../../types/order.type';
import { CollectionSiteSchema } from '../../types/collection-site.type';
import { APP_SHAPE } from '../../constants/shape';
import { useAuthStore } from '../../stores/auth.store';
import { canMutateOpsPickupOrder } from '../../auth/pilot';

const INTL_LOCALES: Record<string, string> = {
  en: 'en-US',
  ar: 'ar-SA',
  ur: 'ur-PK',
  hi: 'hi-IN',
  bn: 'bn-BD',
  si: 'si-LK',
  ne: 'ne-NP',
};

const STATUS_FILTER_OPTIONS = [
  { value: '', labelKey: 'common.all' },
  { value: PickupOrderStatus.PENDING, labelKey: 'pickupOrders.status.pending' },
  { value: PickupOrderStatus.IN_TRANSIT, labelKey: 'pickupOrders.status.inTransit' },
  { value: PickupOrderStatus.COMPLETED, labelKey: 'pickupOrders.status.completed' },
  { value: PickupOrderStatus.CANCELLED, labelKey: 'pickupOrders.status.cancelled' },
] as const;

function getIntlLocale(language: string): string {
  const base = language.split('-')[0];
  return INTL_LOCALES[base] ?? 'en-US';
}

function translateStatus(status: string, t: TFunction): string {
  switch (status?.toLowerCase()) {
    case PickupOrderStatus.PENDING:
      return t('pickupOrders.status.pending');
    case PickupOrderStatus.IN_TRANSIT:
      return t('pickupOrders.status.inTransit');
    case PickupOrderStatus.COMPLETED:
      return t('pickupOrders.status.completed');
    case PickupOrderStatus.CANCELLED:
      return t('pickupOrders.status.cancelled');
    default:
      return status;
  }
}

function translatePriority(priority: string, t: TFunction): string {
  switch (priority?.toLowerCase()) {
    case 'urgent':
      return t('pickupOrders.priority.urgent');
    case 'high':
      return t('pickupOrders.priority.high');
    case 'medium':
      return t('pickupOrders.priority.medium');
    case 'low':
      return t('pickupOrders.priority.low');
    default:
      return priority;
  }
}

function getOrderSiteId(order: OrderSchema): string | null {
  const nested = order.collection_site?.id ?? order.collection_site_id;
  if (nested && typeof nested === 'string') return nested;
  const raw = order.data?.pickup_address;
  if (typeof raw === 'string' && raw) return raw;
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'string') {
    return raw[0];
  }
  return null;
}

function collectionSiteLabel(
  order: OrderSchema,
  sitesById: Map<string, CollectionSiteSchema>
): string {
  const nameFromApi = order.collection_site?.data?.name;
  if (typeof nameFromApi === 'string' && nameFromApi.trim()) {
    return nameFromApi;
  }
  const siteId = getOrderSiteId(order);
  if (!siteId) return 'â€”';
  const site = sitesById.get(siteId);
  if (!site) return 'â€”';
  const n = site.data?.name;
  if (typeof n === 'string' && n.trim()) return n;
  const street = site.data?.address?.street;
  if (typeof street === 'string' && street.trim()) return street;
  return `Site ${siteId.slice(0, 8)}â€¦`;
}

const PickupOrdersActive: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { confirm, confirmDialog } = useConfirm();
  const intlLocale = getIntlLocale(i18n.language);
  const {
    orders,
    ordersLoading,
    ordersError,
    fetchOrders,
    deleteOrder,
    clearOrdersError,
    totalOrders,
  } = usePickupOrderStore();
  const { selectedOrganization } = useOrganizationStore();
  const isPilot = useAuthStore((state) => state.tokenPayload?.is_pilot === true);
  const { canMutate } = useAuthPermissions();
  const {
    isStep,
    tourPhase,
    setTourPhase,
    advanceTourPhase,
    dismissTour,
  } = useSelfSetupTour();
  const tourFiltersHint = isStep('pickup') && tourPhase === 'filters';
  const tourTableHint = isStep('pickup') && tourPhase === 'table';
  const tourAddHint = isStep('pickup') && tourPhase === 'addButton';
  const orgId = selectedOrganization?.id;
  const canCreatePickup = orgId
    ? canMutate(PERM_PICKUP_CREATE(orgId), PICKUP_ORDER_WRITE_ROLES)
    : false;
  const hasWritePickupPermission = orgId
    ? canMutate(PERM_PICKUP_WRITE(orgId), PICKUP_ORDER_WRITE_ROLES)
    : false;
  const canWritePickup = canMutateOpsPickupOrder(
    isPilot,
    hasWritePickupPermission,
  );
  const [dismissCreateHint, setDismissCreateHint] = useState(false);
  const [forceCreateHint, setForceCreateHint] = useState(false);
  const showCreateHint =
    !dismissCreateHint &&
    canCreatePickup &&
    (forceCreateHint || tourAddHint);

  const goToCreate = () => {
    const withSectionHints = forceCreateHint && !tourAddHint;
    setForceCreateHint(false);
    setDismissCreateHint(true);
    if (tourAddHint) {
      setTourPhase('form');
      navigate('/pickup_orders/create');
      return;
    }
    navigate(
      '/pickup_orders/create',
      withSectionHints ? { state: { showSectionHints: true } } : undefined,
    );
  };

  const {
    sites,
    getCollectionSites,
    isLoading: sitesLoading,
    error: sitesError,
  } = useCollectionSiteStore();

  const sitesById = useMemo(() => {
    const m = new Map<string, CollectionSiteSchema>();
    (sites || []).forEach((s) => m.set(s.id, s));
    return m;
  }, [sites]);

  const colCount = 10;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);
  const [appliedStart, setAppliedStart] = useState<string | null>(null);
  const [appliedEnd, setAppliedEnd] = useState<string | null>(null);
  const [dateFilterHint, setDateFilterHint] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createdToastOpen, setCreatedToastOpen] = useState(false);
  /** Knowledge Base "Go to" filter tips. */
  const [helpDateFilterHint, setHelpDateFilterHint] = useState(false);
  const [helpStatusHint, setHelpStatusHint] = useState(false);
  const [helpTableHint, setHelpTableHint] = useState(false);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const appliedSearchRef = useRef(appliedSearch);
  const prevOrgIdRef = useRef<string | undefined>(undefined);

  appliedSearchRef.current = appliedSearch;

  useEffect(() => {
    const state = location.state as {
      orderCreated?: boolean;
      showCreateHint?: boolean;
      showDateFilterHint?: boolean;
      showStatusHint?: boolean;
      showTableHint?: boolean;
    } | null;
    if (
      !state?.orderCreated &&
      !state?.showCreateHint &&
      !state?.showDateFilterHint &&
      !state?.showStatusHint &&
      !state?.showTableHint
    ) {
      return;
    }
    if (state.orderCreated) setCreatedToastOpen(true);
    if (state.showCreateHint) {
      setForceCreateHint(true);
      setDismissCreateHint(false);
    }
    if (state.showDateFilterHint) setHelpDateFilterHint(true);
    if (state.showStatusHint) setHelpStatusHint(true);
    if (state.showTableHint) setHelpTableHint(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        style: 'currency',
        currency: 'SAR',
        maximumFractionDigits: 2,
      }),
    [intlLocale]
  );

  const hasActiveFilters = Boolean(
    appliedSearch || statusFilter || (appliedStart && appliedEnd)
  );

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      const next = searchDraft.trim();
      if (next !== appliedSearchRef.current) {
        setAppliedSearch(next);
        setPage(0);
      }
    }, 400);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchDraft]);

  useEffect(() => {
    if (!selectedOrganization) return;
    void getCollectionSites({ organization_id: selectedOrganization.id });
  }, [selectedOrganization?.id, getCollectionSites]);

  useEffect(() => {
    if (!selectedOrganization) return;

    const orgId = selectedOrganization.id;
    const orgChanged =
      prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== orgId;
    prevOrgIdRef.current = orgId;

    if (orgChanged) {
      setPage(0);
      setDraftStart(null);
      setDraftEnd(null);
      setAppliedStart(null);
      setAppliedEnd(null);
      setDateFilterHint(null);
      setSearchDraft('');
      setAppliedSearch('');
      setStatusFilter('');
      fetchOrders({
        page: 1,
        limit: rowsPerPage,
      });
      return;
    }

    fetchOrders({
      page: page + 1,
      limit: rowsPerPage,
      ...(appliedSearch ? { search: appliedSearch } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(appliedStart && appliedEnd
        ? { start_date: appliedStart, end_date: appliedEnd }
        : {}),
    });
  }, [
    fetchOrders,
    selectedOrganization,
    page,
    rowsPerPage,
    appliedSearch,
    statusFilter,
    appliedStart,
    appliedEnd,
  ]);

  const clearDateFilter = () => {
    setDraftStart(null);
    setDraftEnd(null);
    setAppliedStart(null);
    setAppliedEnd(null);
    setDateFilterHint(null);
    setPage(0);
  };

  const clearAllFilters = () => {
    setSearchDraft('');
    setAppliedSearch('');
    setStatusFilter('');
    clearDateFilter();
    setPage(0);
  };

  useEffect(() => {
    if (!draftStart || !draftEnd) return;
    if (isBefore(startOfDay(draftEnd), startOfDay(draftStart))) {
      setDateFilterHint(t('pickupOrders.filters.endBeforeStart'));
      return;
    }
    const start = format(draftStart, 'yyyy-MM-dd');
    const end = format(draftEnd, 'yyyy-MM-dd');
    if (start === appliedStart && end === appliedEnd) return;
    setDateFilterHint(null);
    setAppliedStart(start);
    setAppliedEnd(end);
    setPage(0);
  }, [draftStart, draftEnd, appliedStart, appliedEnd, t]);

  const handleDelete = async (orderId: string) => {
    if (isPilot) return;
    if (await confirm({ title: t('common.delete'), description: t('pickupOrders.table.confirmDelete'), tone: 'danger' })) {
      await deleteOrder(orderId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(intlLocale);
  };

  if (!selectedOrganization) {
    return (
      <Box>
        <PageHeader icon={<AssignmentIcon />} title={t('pickupOrders.title')} />
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('pickupOrders.selectOrganization')}
        </Alert>
      </Box>
    );
  }

  if (ordersLoading && (!orders || orders.length === 0)) {
    return <LoadingSkeleton variant="table" filters={3} columns={10} rows={6} />;
  }

  return (
    <Box>
      <PageHeader
        icon={<AssignmentIcon />}
        title={t('pickupOrders.title')}
        subtitle={t('pickupOrders.organizationSubtitle', {
          name: selectedOrganization.name,
        })}
        actions={
          canCreatePickup ? (
            <SetupCoachmark
              open={showCreateHint}
              title={
                tourAddHint
                  ? t('selfSetup.tour.pickupAddTitle')
                  : t('pickupOrders.createHintTitle', {
                      defaultValue: 'Create a pickup order',
                    })
              }
              subtitle={
                tourAddHint
                  ? t('selfSetup.tour.pickupAddSubtitle')
                  : t('pickupOrders.createHint', {
                      defaultValue: 'Click New Pickup Order',
                    })
              }
              nextLabel={tourAddHint ? t('selfSetup.tourNext') : undefined}
              onNext={
                tourAddHint
                  ? () => {
                      setDismissCreateHint(true);
                      setTourPhase('form');
                      navigate('/pickup_orders/create');
                    }
                  : undefined
              }
              onDismiss={() => {
                setDismissCreateHint(true);
                setForceCreateHint(false);
                if (tourAddHint) dismissTour();
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={goToCreate}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {t('pickupOrders.newOrder')}
              </Button>
            </SetupCoachmark>
          ) : null
        }
      />

      {(ordersError || sitesError) && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {ordersError && (
            <Alert severity="error" onClose={clearOrdersError}>
              {ordersError}
            </Alert>
          )}
          {sitesError && <Alert severity="error">{sitesError}</Alert>}
        </Stack>
      )}

      <Paper elevation={2} sx={{ ...elevatedPanelSx, mb: 2 }}>
        <SetupCoachmark
          open={tourFiltersHint}
          fullWidth
          title={t('selfSetup.tour.pickupFiltersTitle')}
          subtitle={t('selfSetup.tour.pickupFiltersSubtitle')}
          nextLabel={t('selfSetup.tourNext')}
          onNext={() => advanceTourPhase()}
          onDismiss={() => dismissTour()}
        >
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
          spacing={1.5}
        >
          <TextField
            size="small"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder={t('pickupOrders.filters.searchPlaceholder')}
            sx={{
              flex: '1 1 200px',
              maxWidth: { xs: '100%', md: 360 },
              minWidth: 180,
              '& .MuiOutlinedInput-root': {},
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchDraft ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t('pickupOrders.filters.clearFilters')}
                    onClick={() => setSearchDraft('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <SetupCoachmark
            open={helpStatusHint && !tourFiltersHint}
            title={t('pickupOrders.statusHintTitle')}
            subtitle={t('pickupOrders.statusHint')}
            nextLabel={t('selfSetup.tourDone')}
            onNext={() => setHelpStatusHint(false)}
            onDismiss={() => setHelpStatusHint(false)}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={statusFilter}
              onChange={(_, value) => {
                if (value !== null) {
                  setStatusFilter(value);
                  setPage(0);
                }
              }}
              sx={{
                flexShrink: 0,
                height: 40,
                bgcolor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: `${APP_SHAPE.radiusMd}px`,
                p: '3px',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                '& .MuiToggleButtonGroup-grouped': {
                  border: 0,
                  borderRadius: '6px !important',
                  mx: 0,
                  px: { xs: 1, sm: 1.25 },
                  py: 0.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  lineHeight: 1.4,
                  whiteSpace: 'nowrap',
                  color: theme.palette.text.secondary,
                  gap: 0.5,
                  '&.Mui-selected': {
                    bgcolor: theme.palette.background.paper,
                    boxShadow: theme.shadows[1],
                    '&:hover': { bgcolor: theme.palette.background.paper },
                  },
                  '&:not(.Mui-selected):hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.text.primary,
                  },
                },
              }}
            >
              {STATUS_FILTER_OPTIONS.map((option) => {
                const color = getOperationalStatusColor(option.value);
                const selected = statusFilter === option.value;
                return (
                  <ToggleButton
                    key={option.value || 'all'}
                    value={option.value}
                    sx={{
                      '&.Mui-selected': {
                        color: `${color} !important`,
                      },
                    }}
                  >
                    {selected ? (
                      <Box
                        component="span"
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: color,
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    {t(option.labelKey)}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </SetupCoachmark>

          <SetupCoachmark
            open={helpDateFilterHint && !tourFiltersHint}
            title={t('pickupOrders.dateFilterHintTitle')}
            subtitle={t('pickupOrders.dateFilterHint')}
            nextLabel={t('selfSetup.tourDone')}
            onNext={() => setHelpDateFilterHint(false)}
            onDismiss={() => setHelpDateFilterHint(false)}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                flexShrink: 0,
                px: 1,
                py: 0.5,
                borderRadius: `${APP_SHAPE.radiusMd}px`,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.default,
              }}
            >
              <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary', ml: 0.25 }} />
              <DatePicker
                value={draftStart}
                onChange={(v) => setDraftStart(v)}
                slotProps={{
                  textField: {
                    size: 'small',
                    placeholder: t('pickupOrders.filters.from'),
                    sx: {
                      width: 118,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: theme.palette.background.paper,
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    },
                  },
                }}
              />
              <Typography variant="body2" color="text.disabled" sx={{ userSelect: 'none' }}>
                –
              </Typography>
              <DatePicker
                value={draftEnd}
                onChange={(v) => setDraftEnd(v)}
                slotProps={{
                  textField: {
                    size: 'small',
                    placeholder: t('pickupOrders.filters.to'),
                    sx: {
                      width: 118,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: theme.palette.background.paper,
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    },
                  },
                }}
              />
              {(draftStart || draftEnd || appliedStart) && (
                <Tooltip title={t('pickupOrders.filters.clearDates')}>
                  <IconButton
                    size="small"
                    onClick={clearDateFilter}
                    aria-label={t('pickupOrders.filters.clearDates')}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </SetupCoachmark>

          {hasActiveFilters && (
            <Button
              size="small"
              color="inherit"
              startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              onClick={clearAllFilters}
              sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
            >
              {t('pickupOrders.filters.clearFilters')}
            </Button>
          )}
        </Stack>

        {dateFilterHint && (
          <Alert
            severity="warning"
            onClose={() => setDateFilterHint(null)}
            sx={{ py: 0, mt: 1.25 }}
          >
            {dateFilterHint}
          </Alert>
        )}
        </Box>
        </SetupCoachmark>

        {ordersLoading && <LinearProgress />}
        <SetupCoachmark
          open={helpTableHint || tourTableHint}
          fullWidth
          title={
            tourTableHint
              ? t('selfSetup.tour.pickupTableTitle')
              : t('pickupOrders.tableHintTitle')
          }
          subtitle={
            tourTableHint
              ? t('selfSetup.tour.pickupTableSubtitle')
              : t('pickupOrders.tableHint')
          }
          nextLabel={
            tourTableHint ? t('selfSetup.tourNext') : t('selfSetup.tourDone')
          }
          onNext={() => {
            if (tourTableHint) advanceTourPhase();
            else setHelpTableHint(false);
          }}
          onDismiss={() => {
            if (tourTableHint) dismissTour();
            else setHelpTableHint(false);
          }}
        >
          <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
          <Table
            stickyHeader
            sx={{
              '& .MuiTableCell-head': {
                py: 1.5,
                px: 2,
              },
              '& .MuiTableCell-body': {
                py: 1.75,
                px: 2,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('pickupOrders.table.id')}</TableCell>
                <TableCell>{t('pickupOrders.table.collectionSite')}</TableCell>
                <TableCell>{t('pickupOrders.table.wasteType')}</TableCell>
                <TableCell>{t('pickupOrders.table.materialType')}</TableCell>
                <TableCell align="right">{t('pickupOrders.table.amount')}</TableCell>
                <TableCell align="right">{t('pickupOrders.table.saleAmount')}</TableCell>
                <TableCell>{t('pickupOrders.table.status')}</TableCell>
                <TableCell>{t('pickupOrders.table.priority')}</TableCell>
                <TableCell>{t('pickupOrders.table.created')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!orders || orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} sx={{ py: 8, border: 0 }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.text.secondary, 0.08),
                          color: theme.palette.text.secondary,
                        }}
                      >
                        <InboxIcon fontSize="large" />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t('pickupOrders.table.emptyTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('pickupOrders.table.emptyDescription', {
                          name: selectedOrganization.name,
                        })}
                      </Typography>
                      {canCreatePickup ? (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={goToCreate}
                          sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                        >
                          {t('pickupOrders.create.submit')}
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => {
                  const isPending =
                    (order.status || '').toLowerCase() === 'pending';
                  return (
                    <TableRow
                      key={order.id}
                      sx={{
                        '&:hover': {
                          bgcolor: theme.palette.action.hover,
                        },
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            color: theme.palette.text.secondary,
                          }}
                        >
                          #{order.id.slice(order.id.length - 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {sitesLoading
                            ? 'â€¦'
                            : collectionSiteLabel(order, sitesById)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={order.waste_type}
                          sx={{
                            height: 26,
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            border: `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.25
                            )}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.material_type?.name || t('pickupOrders.table.notAvailable')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {order.amount} {order.amount_unit || ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {currencyFormatter.format(Number(order.sale_amount || 0))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          value={order.status}
                          label={translateStatus(order.status, t)}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          kind="priority"
                          value={order.data?.priority || 'medium'}
                          label={translatePriority(
                            order.data?.priority || 'medium',
                            t
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(order.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {isPending && canWritePickup ? (
                          <Tooltip title={t('pickupOrders.table.deleteOrder')}>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(order.id)}
                              sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.12),
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </SetupCoachmark>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 20, 50, 100]}
          count={totalOrders}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage={t('pickupOrders.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            t('pickupOrders.pagination.displayedRows', { from, to, count })
          }
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Snackbar
        open={createdToastOpen}
        autoHideDuration={3500}
        onClose={() => setCreatedToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setCreatedToastOpen(false)}
          sx={{ width: '100%' }}
        >
          {t('pickupOrders.create.successToast')}
        </Alert>
      </Snackbar>
      {confirmDialog}
    </Box>
  );
};

export default PickupOrdersActive;
